import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Package, Warning, ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ItemFormDialog, UsageDialog } from "@/components/accounting/InventoryDialogs";

export default function InventoryTab() {
  const [items, setItems] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [usageItem, setUsageItem] = useState(null);

  const load = () => {
    api.get("/accounting/inventory").then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/accounting/inventory/alerts").then(({ data }) => setAlerts(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setEditOpen(true); };
  const openEdit = (item) => { setEditing(item); setEditOpen(true); };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    try {
      await api.delete(`/accounting/inventory/${item.id}`);
      toast.success("Item deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="inventory-tab">
      {alerts.length > 0 && (
        <div className="flex items-start gap-3 border border-status-pending/50 bg-status-pending/10 p-4" data-testid="low-stock-alert">
          <Warning size={22} weight="fill" className="mt-0.5 shrink-0 text-status-pending" />
          <div>
            <div className="font-heading font-bold text-status-pending">Low stock alert — {alerts.length} item{alerts.length > 1 ? "s" : ""} at or below reorder level</div>
            <div className="mt-1 text-sm text-muted-foreground">
              {alerts.map((a) => `${a.name} (${a.quantity} ${a.unit} left, reorder at ${a.reorder_level})`).join(" · ")}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Store-room stock, usage logging and reorder alerts.</p>
        <Button onClick={openCreate} data-testid="add-item-button" className="transition-colors">
          <Plus size={16} className="mr-2" /> Add item
        </Button>
      </div>

      <div className="overflow-x-auto border border-border bg-card">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="p-3 font-medium">Item</th>
              <th className="p-3 font-medium">Category</th>
              <th className="p-3 text-right font-medium">In stock</th>
              <th className="p-3 text-right font-medium">Reorder level</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(items || []).map((item) => {
              const low = item.quantity <= item.reorder_level;
              return (
                <tr key={item.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`inventory-row-${item.id}`}>
                  <td className="p-3 font-medium">{item.name}</td>
                  <td className="p-3 capitalize text-muted-foreground">{item.category}</td>
                  <td className="p-3 text-right font-medium" data-testid={`inventory-qty-${item.id}`}>{item.quantity} {item.unit}</td>
                  <td className="p-3 text-right text-muted-foreground">{item.reorder_level} {item.unit}</td>
                  <td className="p-3" data-testid={`inventory-status-${item.id}`}>
                    {low ? (
                      <span className="inline-flex items-center gap-1 rounded-sm border border-status-pending/40 bg-status-pending/10 px-2 py-0.5 text-xs font-medium text-status-pending">
                        <Warning size={12} weight="fill" /> Low stock
                      </span>
                    ) : (
                      <span className="rounded-sm border border-status-confirmed/30 bg-status-confirmed/10 px-2 py-0.5 text-xs font-medium text-status-confirmed">In stock</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" data-testid={`log-usage-${item.id}`}
                        onClick={() => setUsageItem(item)} className="transition-colors hover:border-primary hover:text-primary">
                        <ArrowsClockwise size={14} className="mr-1" /> Log usage
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`edit-item-${item.id}`} onClick={() => openEdit(item)} className="transition-colors">
                        <PencilSimple size={14} />
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`delete-item-${item.id}`} onClick={() => remove(item)}
                        className="transition-colors hover:border-destructive hover:text-destructive">
                        <Trash size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {items && items.length === 0 && (
          <div className="flex flex-col items-center gap-2 p-10 text-sm text-muted-foreground" data-testid="inventory-empty">
            <Package size={28} /> No items in the store room yet.
          </div>
        )}
      </div>

      <ItemFormDialog open={editOpen} onOpenChange={setEditOpen} editing={editing} onSaved={load} />
      <UsageDialog item={usageItem} onClose={() => setUsageItem(null)} onSaved={load} />
    </div>
  );
}
